import React from 'react';
import { useSelector } from 'react-redux';
import {
  Section,
  HighlightsArticleList,
  SixSixArticleList,
  SectionHeader,
  AppContent,
  Ad,
  ErrorBoundary,
  H1,
  H2,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import {
  LoadMoreButton,
  useFetchNextPosts,
} from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getSectionArticlesByKey, getSectionDisplayNameByKey, getSectionNextByKey, getSectionTextByKey } from '../../store/template/feedPage/feedpage.selectors';
import { HeaderConnected } from '../../components/connectedComponents/HeaderConnected';
import { FooterConnected } from '../../components/connectedComponents/FooterConnected';
import { MainWrapperConnected } from '../../components/connectedComponents/MainWrapperConnected';
import { getAdSlotId } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppState, AppActions } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';

interface FeedPageProps {
  store: Store<AppState, AppActions>
  errorProvider: ErrorReporter
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey('topSection')(state) || [];
  const feedSectionArticles = getSectionArticlesByKey('feedSection')(state) || [];
  return [
    ...topSectionArticles,
    ...feedSectionArticles,
  ];
};

export const FeedPageComponent: React.FunctionComponent<FeedPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const FeedPageContentConnected: React.FunctionComponent = props => {
  const topSectionDisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const cardsSectionDisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.FEED_SECTION));
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));

  return (
    <React.Fragment>
      <MetaTags articles={articles} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <HeaderConnected />
          <MainWrapperConnected>
            <Ad id={topAdId} />
            <Section>
              <SectionHeader linkText={null} href={null}>
                <H1>{topSectionDisplayName}</H1>
              </SectionHeader>
              <HighlightsArticleList cardsProps={topSectionArticles} />
            </Section>
            <Section>
              <SectionHeader linkText={null} href={null}>
                <H2>{cardsSectionDisplayName}</H2>
              </SectionHeader>
              <SixSixArticleList cardsProps={cardsSectionArticles} />
            </Section>
            <LoadMoreFeedPostsButtonConnected />
            <Ad id={oneOnOneId} />
          </MainWrapperConnected>
          <FooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};


export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = props => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getSectionNextByKey(sectionKey));
  const text = useSelector(getSectionTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getSectionNextByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
